/**
 * Endpoints de **Finanzas → Movimientos de cuenta** (`/api/finanzas/movimientos`).
 *
 * El listado va paginado en el servidor con los filtros del panel anterior
 * (fechas, banco y cuenta), la pestaña de activos o anulados y el buscador; el
 * detalle de una fila trae su reparto («dispersión») y, si es una visa POS, los
 * cobros que la componen. Las exportaciones (CSV y PDF) y el comprobante se piden
 * con el token de la sesión.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CatalogosMovimientos,
  MovimientoDetalle,
  MovimientosFiltros,
  MovimientosRespuesta,
} from './movimientos.types';

export const movimientosKeys = {
  lista: (filtros: MovimientosFiltros) =>
    ['finanzas', 'movimientos', filtros] as const,
  catalogos: () => ['finanzas', 'movimientos', 'catalogos'] as const,
  detalle: (id: number) => ['finanzas', 'movimientos', 'detalle', id] as const,
};

/** Arma la cadena de consulta del listado y de las exportaciones. */
export function cadenaDeMovimientos(filtros: MovimientosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.banco) parametros.set('banco', String(filtros.banco));
  if (filtros.cuenta) parametros.set('cuenta', String(filtros.cuenta));
  if (filtros.estado === 'anulados') parametros.set('estado', 'anulados');
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const movimientosApi = {
  listar: (
    filtros: MovimientosFiltros,
    signal?: AbortSignal,
  ): Promise<MovimientosRespuesta> =>
    apiClient.get<MovimientosRespuesta>(
      `/api/finanzas/movimientos${cadenaDeMovimientos(filtros)}`,
      { signal },
    ),

  catalogos: (signal?: AbortSignal): Promise<CatalogosMovimientos> =>
    apiClient.get<CatalogosMovimientos>('/api/finanzas/movimientos/catalogos', {
      signal,
    }),

  detalle: (id: number, signal?: AbortSignal): Promise<MovimientoDetalle> =>
    apiClient.get<MovimientoDetalle>(`/api/finanzas/movimientos/${id}`, { signal }),

  anular: (id: number): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/finanzas/movimientos/${id}/anular`),

  /** Ruta del comprobante (se abre con `abrirPdfAutenticado`). */
  urlComprobante: (id: number): string =>
    `/finanzas/movimientos/${id}/comprobante`,

  /** Ruta de la exportación (CSV o PDF). */
  urlExportar: (filtros: MovimientosFiltros, formato: 'csv' | 'pdf'): string => {
    const ruta = formato === 'csv' ? 'exportar' : 'pdf';

    return `/finanzas/movimientos/${ruta}${cadenaDeMovimientos({ ...filtros, page: 1, perPage: 0 })}`;
  },
};

/** Movimientos del filtro pedido. */
export function useMovimientos(filtros: MovimientosFiltros) {
  return useQuery({
    queryKey: movimientosKeys.lista(filtros),
    queryFn: ({ signal }) => movimientosApi.listar(filtros, signal),
    // Al cambiar de página o de pestaña se mantiene lo anterior a la vista.
    placeholderData: (anterior) => anterior,
  });
}

/** Bancos, cuentas y tipos de movimiento. */
export function useCatalogosMovimientos() {
  return useQuery({
    queryKey: movimientosKeys.catalogos(),
    queryFn: ({ signal }) => movimientosApi.catalogos(signal),
    staleTime: 5 * 60 * 1000,
  });
}

/** Detalle de un movimiento (se pide al abrir el diálogo). */
export function useMovimientoDetalle(id: number | null) {
  return useQuery({
    queryKey: movimientosKeys.detalle(id ?? 0),
    queryFn: ({ signal }) => movimientosApi.detalle(id as number, signal),
    enabled: id !== null,
  });
}

/** Anula un movimiento y refresca el listado. */
export function useAnularMovimiento() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => movimientosApi.anular(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['finanzas', 'movimientos'] });
    },
  });
}
